import type {
  CpscEntity,
  CpscItem,
  CpscEntityInput,
  EntityRoleCode,
  CpscItemInput,
} from '__generated__/graphql';
import { EntityRoleCode as EntityRoleCodeEnum } from '__generated__/graphql';
import { transformCitations, CitationFormValue } from './transformCitations';

/**
 * Form state type for CpscEntity with citations transformed to labelInValue format.
 * testingInclusions/testingExclusions use CitationFormValue[] instead of Citation[].
 */
export type CpscEntityFormState = Omit<
  CpscEntity,
  'testingInclusions' | 'testingExclusions'
> & {
  testingInclusions?: CitationFormValue[];
  testingExclusions?: CitationFormValue[];
};

/**
 * Laboratory role codes that can appear in the laboratories array
 */
const LABORATORY_ROLE_CODES: EntityRoleCode[] = [
  EntityRoleCodeEnum.ITL,
  EntityRoleCodeEnum.LAB,
  EntityRoleCodeEnum.NOL,
];

/**
 * Transform CPSC entities between GraphQL schema shape and UI form shape.
 *
 * Schema shape: { manufacturers[], laboratories[], certifyingEntities[], pointOfContacts[] }
 * Form shape:   { entities[] } - flat array with entityRoleCode determining type
 *
 * This allows the UI to use a simple add/remove entities pattern while
 * maintaining compatibility with the structured GraphQL schema.
 */
export const transformCpscEntities = {
  /**
   * Transform GraphQL schema → Form state
   * Combines all 4 entity arrays into flat entities[] array for UI
   * @param {CpscItem} item - CpscItem with entity arrays
   * @return {CpscEntityFormState[]} Flat array of entities with citations transformed to form format
   */
  toForm: (item: CpscItem): CpscEntityFormState[] => {
    const entities: CpscEntityFormState[] = [];

    // Helper to transform citations from schema format to form format
    const transformEntity = (entity: CpscEntity): CpscEntityFormState => ({
      ...entity,
      testingInclusions: transformCitations.toForm(entity.testingInclusions),
      testingExclusions: transformCitations.toForm(entity.testingExclusions),
    });

    if (item.manufacturers?.length) {
      entities.push(...item.manufacturers.map(transformEntity));
    }
    if (item.laboratories?.length) {
      entities.push(...item.laboratories.map(transformEntity));
    }
    if (item.certifyingEntities?.length) {
      entities.push(...item.certifyingEntities.map(transformEntity));
    }
    if (item.pointOfContacts?.length) {
      entities.push(...item.pointOfContacts.map(transformEntity));
    }

    return entities;
  },

  /**
   * Transform Form state → GraphQL schema
   * Splits flat entities[] array into 4 structured arrays by entityRoleCode
   * @param {CpscEntityFormState[]} entities - Flat array of entities with form-format citations
   * @return {Partial<CpscItemInput>} CpscItem with entity arrays grouped by role
   */
  toSchema: (entities: CpscEntityFormState[]): Partial<CpscItemInput> => {
    if (!entities?.length) {
      return {};
    }

    const result: Partial<CpscItemInput> = {};

    // Helper to transform citations from form format back to schema format
    const transformEntity = (entity: CpscEntityFormState): CpscEntityInput =>
      ({
        ...entity,
        testingInclusions: transformCitations.toSchema(
          entity.testingInclusions,
        ),
        testingExclusions: transformCitations.toSchema(
          entity.testingExclusions,
        ),
      } as CpscEntityInput);

    const manufacturers = entities.filter(
      (e) => e.entityRoleCode === EntityRoleCodeEnum.MF,
    );
    if (manufacturers.length) {
      result.manufacturers = manufacturers.map(transformEntity);
    }

    const laboratories = entities.filter((e) =>
      LABORATORY_ROLE_CODES.includes(e.entityRoleCode as EntityRoleCode),
    );
    if (laboratories.length) {
      result.laboratories = laboratories.map(transformEntity);
    }

    const certifyingEntities = entities.filter(
      (e) => e.entityRoleCode === EntityRoleCodeEnum.CE,
    );
    if (certifyingEntities.length) {
      result.certifyingEntities = certifyingEntities.map(transformEntity);
    }

    const pointsOfContact = entities.filter(
      (e) => e.entityRoleCode === EntityRoleCodeEnum.PK,
    );
    if (pointsOfContact.length) {
      result.pointOfContacts = pointsOfContact.map(transformEntity);
    }

    return result;
  },
};
